import { captureException } from "@sentry/react";

import { callWithTimeout, detectCookiesEnabled, parseCookies } from "./cookies";

declare global {
  interface Window {
    [index: string]: any;
    gtag?: (...args: any[]) => void;
    _gtagSet?: boolean;
    _gtagInitialized?: boolean;
    _gtagError?: Event;
  }
}

type GTMEcommerceImpression = {
  name: string;
  id: number;
  price: number;
  brand: string;
  category: string;
  variant: string;
  list: string;
  position?: number;
};

type GTMEcommerceLayer =
  | {
      ecommerce: {
        currencyCode: string;
        impressions: GTMEcommerceImpression[];
      };
      event: "gtm-ee-event";
      "gtm-ee-event-category": string;
      "gtm-ee-event-action": "Product Impressions";
      "gtm-ee-event-non-interaction": string;
      "gtm.uniqueEventId": number;
    }
  | {
      ecommerce: {
        currencyCode: string;
        detail: {
          actionField: {
            list: string;
          };
          products: {
            name: string;
            id: number;
            price: number;
            brand: string;
            category: string;
            variant: string;
          }[];
        };
      };
      event: "gtm-ee-event";
      "gtm-ee-event-category": string;
      "gtm-ee-event-action": "Product Details";
      "gtm-ee-event-non-interaction": string;
      "gtm.uniqueEventId": number;
    };

type GTMDataLayer =
  | {
      event: string;
      [key: string]: any;
    }
  | {
      event: "virtualPageview";
      pageUrl: string;
      pageTitle: string;
      [key: string]: any;
    }
  | GTMEcommerceLayer;

declare global {
  interface Window {
    [index: string]: any;
    dataLayer: GTMDataLayer[];
    gtag?: (...args: any[]) => void;
    _gtagSet?: boolean;
    _gtagInitialized?: boolean;
    _gtagError?: Event;
  }
}
type GTEvent =
  | {
      event: "page_view";
      page_location: string;
      page_title: string;
    }
  | {
      event: "search";
      search_term: string;
      search_count: number;
      items: any;
    }
  | {
      event:
        | "submit-appointment-form"
        | "submit-appointment-form-validated"
        | "submit-appointment-form-success";
    }
  | {
      event: "begin_checkout";
      currency: string;
      shipping: number;
      tax: number;
      value: number;
      user_data?: unknown;
    }
  | {
      event: "form_submit";
      user_data?: unknown;
    }
  | { event: "profile_floating_booking" }
  | {
      event: "reschedule_after_visit";
    }
  | {
      event: "reschedule_change_time";
    }
  | {
      event: "reschedule_after_visit_success";
    }
  | {
      event: "reschedule_change_time_success";
    };
export const gtagId = `G-${import.meta.env.DOQ_GOOGLE_TAG_ID}`;

export async function parseGoogleSessionData() {
  if (!import.meta.env.DOQ_GOOGLE_TAG_ID) return undefined;

  try {
    const cookies = parseCookies();

    const _ga: string | undefined = cookies["_ga"];
    const _ga_session: string | undefined =
      cookies["_ga_" + import.meta.env.DOQ_GOOGLE_TAG_ID];
    let client_id: string | undefined = undefined;
    let session_id: string | undefined = undefined;
    let session_number: string | undefined = undefined;

    // Parse version 1 _ga client id
    if (_ga?.includes("GA1")) {
      // GA1.1.2002560640.1746517969
      // Skip first 6 symbols
      client_id = _ga.substring(6);
    }

    // Parse version 1 _ga session data
    if (_ga_session?.includes("GS1")) {
      // GS1.1.1746825440.14.0.17468254406.0.0.295082955
      const parts = _ga_session.split(".");

      session_id = parts[2];
      session_number = parts[3];
    }

    // Parse version 2 _ga session data
    if (_ga_session?.includes("GS2")) {
      // GS2.1.s1750233764$o2$g1$t1750235972$j10$l0$h0
      const segments = Object.fromEntries(
        _ga_session
          .substring(6)
          .split("$")
          .map((pair) => [pair[0], pair.slice(1)]),
      );
      session_id = segments["s"];
      session_number = segments["o"];
    }

    // Fallback in parallel
    await Promise.all([
      (async () => {
        if (!client_id && window.gtag) {
          client_id = await callWithTimeout((callback) => {
            window.gtag?.("get", gtagId, "client_id", callback);
          });
        }
      })(),
      (async () => {
        if (!session_id && window.gtag) {
          session_id = await callWithTimeout((callback) => {
            window.gtag?.("get", gtagId, "session_id", callback);
          });
        }
      })(),
      (async () => {
        if (!session_number && window.gtag) {
          session_number = await callWithTimeout((callback) => {
            window.gtag?.("get", gtagId, "session_number", callback);
          });
        }
      })(),
    ]);

    // Without client id, parsing everything else is not required
    if (!client_id) {
      throw new Error("Couldn't parse ga cookies");
    }
    return {
      client_id,
      session_id,
      session_number,
    };
  } catch (error) {
    const cookies = parseCookies();
    captureException(error, {
      level: Object.keys(cookies).some((key) => key.startsWith("_ga"))
        ? "error"
        : "warning",
      tags: { analytics: "_ga-cookie-parsing" },
      extra: {
        error: {
          type: typeof error,
          message: error instanceof Error ? error.message : undefined,
          data: JSON.stringify(error),
        },
        data: cookies,
        isGtagDefined: Boolean(window.gtag),
        cookiesEnabled: await detectCookiesEnabled(),
        isRobot: window.isRobot,
        _gtagSet: window._gtagSet,
        _gtagInitialized: window._gtagInitialized,
        _gtagError: window._gtagError,
        _gtagErrorMessage:
          window._gtagError && "message" in window._gtagError
            ? window._gtagError.message
            : undefined,
        consent: window.Cookiebot?.consent,
      },
    });
  }
  return undefined;
}
(window as any).parseGoogleSessionData = parseGoogleSessionData;

const last: Record<string, string> = {};
export const useGtag = () => {
  return {
    parseSessionData: parseGoogleSessionData,
    setParameter: (field: string, data: unknown) => {
      window.gtag?.("set", field, data);
    },
    sendEvent: ({
      event,
      allowDuplicate = true,
      ...params
    }: GTEvent & {
      /**
       * Whether to allow for back-to-back duplicate events or not
       *
       * @default true
       */
      allowDuplicate?: boolean;
    }) => {
      const token = JSON.stringify(params);
      if (!allowDuplicate && last[event] === token) return;
      last[event] = token;
      window.gtag?.("event", event, params);
      if (import.meta.env.DOQ_ENVIRONMENT === "development") {
        // eslint-disable-next-line no-console
        console.info("dataLayer push:", event, params);
      }
    },
  };
};
